// İSG uzmanı portalı — Eğitim oluştur/düzenle editörü (api-contract §16.1). Tek sayfa:
// **meta** (başlık/açıklama/geçme eşiği/durum) + **video yükleme** (§8 presigned; VideoUploadField)
// + **quiz editörü** (QuizEditor — dinamik sorular + seçenekler + doğru cevap). Kaydet →
// `POST /trainings` (create) / `PUT /trainings/{uuid}` (update; soru seti replace).
//
// **⛔ correctOptionIndex — yalnız bu editör.** Doğru cevap yalnız authoring'de görünür/düzenlenir
// (İSG uzmanı). Personel/mobil bağlamı (Faz B) ASLA görmez.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  CardContent,
  Field,
  Input,
  PageHeader,
  Spinner,
  type TrainingDetailDto,
  resolveApiErrorMessage,
  trainingApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GraduationCap } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { QuizEditor } from '../../components/trainings/QuizEditor';
import { VideoUploadField } from '../../components/trainings/VideoUploadField';
import {
  MAX_OPTIONS,
  MIN_OPTIONS,
  type TrainingEditorForm,
  emptyQuestion,
} from '../../components/trainings/trainingEditorForm';
import { trainingKeys } from '../../lib/trainingKeys';

export function TrainingEditorPage() {
  const { t } = useTranslation();
  const { trainingUuid } = useParams();
  const isEdit = Boolean(trainingUuid) && trainingUuid !== 'new';

  const query = useQuery({
    queryKey: trainingKeys.detail(trainingUuid ?? ''),
    queryFn: () => trainingApi.getTraining(trainingUuid ?? ''),
    enabled: isEdit,
  });

  return (
    // `pb-20` (owner review B2 item 4) — aşağıdaki `TrainingEditorBody`'nin yapışkan alt aksiyon
    // çubuğu için (`RiskAssessmentEditorPage.tsx`'in AYNI deseni).
    <div className="flex flex-col gap-6 pb-20">
      <Breadcrumb
        items={[
          { label: t('trainings.title'), href: '/trainings' },
          {
            label: isEdit
              ? (query.data?.title ?? t('trainings.editor.editTitle'))
              : t('trainings.editor.createTitle'),
          },
        ]}
      />

      {isEdit && query.isPending ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Spinner label={t('trainings.editor.loading')} />
        </div>
      ) : isEdit && query.isError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
            <GraduationCap className="h-10 w-10 text-danger" aria-hidden="true" />
            <p className="text-sm text-muted">{resolveApiErrorMessage(query.error, t)}</p>
            <Button asChild variant="secondary">
              <Link to="/trainings">{t('trainings.editor.back')}</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        // `key` ile taze durum: yeni ↔ düzenle ve farklı eğitim arasında form/video sıfırlanır.
        <TrainingEditorBody
          key={isEdit ? trainingUuid : 'new'}
          initial={isEdit ? (query.data ?? null) : null}
        />
      )}
    </div>
  );
}

/** Detay yüklendikten (ya da create) SONRA bir kez kurulur; durum props'tan lazy başlatılır. */
function TrainingEditorBody({ initial }: { initial: TrainingDetailDto | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = initial !== null;

  // Video referansı form dışı yerel durumdur (§8 link; VideoUploadField yönetir).
  const [videoDocumentUuid, setVideoDocumentUuid] = useState<string | null>(
    initial?.videoDocumentUuid ?? null,
  );
  const [videoFileName, setVideoFileName] = useState<string | null>(initial?.videoFileName ?? null);

  const schema = useMemo(
    () =>
      z
        .object({
          title: z.string().trim().min(1, t('trainings.editor.validation.titleRequired')),
          description: z.string().trim(),
          passThreshold: z.number({
            invalid_type_error: t('trainings.editor.validation.passThresholdRange'),
          }),
          isActive: z.boolean(),
          questions: z
            .array(
              z
                .object({
                  text: z.string().trim().min(1, t('trainings.editor.validation.questionRequired')),
                  options: z
                    .array(
                      z.object({
                        text: z
                          .string()
                          .trim()
                          .min(1, t('trainings.editor.validation.optionRequired')),
                      }),
                    )
                    .min(MIN_OPTIONS, t('trainings.editor.validation.optionsRange'))
                    .max(MAX_OPTIONS, t('trainings.editor.validation.optionsRange')),
                  correctOptionIndex: z
                    .number({
                      invalid_type_error: t('trainings.editor.validation.correctRequired'),
                    })
                    .int()
                    .min(0),
                })
                .superRefine((question, ctx) => {
                  if (question.correctOptionIndex >= question.options.length) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      path: ['correctOptionIndex'],
                      message: t('trainings.editor.validation.correctRange'),
                    });
                  }
                }),
            )
            .min(1, t('trainings.editor.validation.atLeastOneQuestion')),
        })
        .superRefine((values, ctx) => {
          if (
            Number.isNaN(values.passThreshold) ||
            values.passThreshold < 1 ||
            values.passThreshold > values.questions.length
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['passThreshold'],
              message: t('trainings.editor.validation.passThresholdRange'),
            });
          }
        }),
    [t],
  );

  const defaultValues: TrainingEditorForm = useMemo(
    () => ({
      title: initial?.title ?? '',
      description: initial?.description ?? '',
      passThreshold: initial?.passThreshold ?? 1,
      isActive: initial?.isActive ?? true,
      questions:
        initial && initial.questions.length > 0
          ? initial.questions.map((question) => ({
              text: question.text,
              options: question.options.map((option) => ({ text: option })),
              correctOptionIndex: question.correctOptionIndex,
            }))
          : [emptyQuestion()],
    }),
    [initial],
  );

  const methods = useForm<TrainingEditorForm>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const mutation = useMutation({
    mutationFn: (values: TrainingEditorForm) => {
      const body = {
        title: values.title.trim(),
        description: values.description.trim().length > 0 ? values.description.trim() : null,
        videoDocumentUuid,
        passThreshold: values.passThreshold,
        isActive: values.isActive,
        questions: values.questions.map((question, index) => ({
          order: index + 1,
          text: question.text.trim(),
          options: question.options.map((option) => option.text.trim()),
          correctOptionIndex: question.correctOptionIndex,
        })),
      };
      return isEdit && initial
        ? trainingApi.updateTraining(initial.uuid, body)
        : trainingApi.createTraining(body);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: trainingKeys.list() });
      queryClient.invalidateQueries({ queryKey: trainingKeys.access() });
      queryClient.invalidateQueries({ queryKey: trainingKeys.detail(saved.uuid) });
      navigate('/trainings');
    },
  });

  return (
    <FormProvider {...methods}>
      <PageHeader
        title={isEdit ? t('trainings.editor.editTitle') : t('trainings.editor.createTitle')}
      />

      <form
        className="flex flex-col gap-6"
        onSubmit={methods.handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {/* `max-w-[960px]` (owner review B2 item 4) — okunur form sütunu genişliği. owner review
            B4-3: sticky aksiyon çubuğu artık BU sarmalayıcının İÇİNDE (son çocuk) — önceki sürüm
            çubuğu `<form>`'un tam genişliğine, sütunun DIŞINA koyuyordu (Dashboard'ın `mx-auto`
            KULLANMAYAN sola yaslı konvansiyonu HÂLÂ geçerli, ama çubuk artık forma bağlı). */}
        <div className="flex max-w-[960px] flex-col gap-6">
          {mutation.isError ? (
            <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
          ) : null}

          {/* Meta */}
          <Card>
            <CardContent>
              <fieldset className="flex flex-col gap-4">
                <legend className="text-sm font-semibold text-fg">
                  {t('trainings.editor.section.meta')}
                </legend>
                <Field
                  htmlFor="training-title"
                  label={t('trainings.editor.field.title')}
                  error={methods.formState.errors.title?.message}
                  required
                >
                  <Input
                    id="training-title"
                    invalid={Boolean(methods.formState.errors.title)}
                    {...methods.register('title')}
                  />
                </Field>
                <Field
                  htmlFor="training-description"
                  label={t('trainings.editor.field.description')}
                >
                  <Input id="training-description" {...methods.register('description')} />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    htmlFor="training-pass-threshold"
                    label={t('trainings.editor.field.passThreshold')}
                    error={methods.formState.errors.passThreshold?.message}
                    hint={t('trainings.editor.field.passThresholdHint')}
                    required
                  >
                    <Input
                      id="training-pass-threshold"
                      type="number"
                      min={1}
                      invalid={Boolean(methods.formState.errors.passThreshold)}
                      {...methods.register('passThreshold', { valueAsNumber: true })}
                    />
                  </Field>
                  <div className="flex items-end">
                    <label className="flex cursor-pointer items-center gap-2 pb-2.5 text-sm text-fg">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        {...methods.register('isActive')}
                      />
                      {t('trainings.editor.field.isActive')}
                    </label>
                  </div>
                </div>
              </fieldset>
            </CardContent>
          </Card>

          {/* Video (§8) */}
          <Card>
            <CardContent>
              <fieldset className="flex flex-col gap-3">
                <legend className="text-sm font-semibold text-fg">
                  {t('trainings.editor.section.video')}
                </legend>
                <p className="-mt-1 text-xs text-muted">
                  {t('trainings.editor.video.description')}
                </p>
                <VideoUploadField
                  videoDocumentUuid={videoDocumentUuid}
                  videoFileName={videoFileName}
                  disabled={mutation.isPending}
                  onChange={(uuid, fileName) => {
                    setVideoDocumentUuid(uuid);
                    setVideoFileName(fileName);
                  }}
                />
              </fieldset>
            </CardContent>
          </Card>

          {/* Quiz (§16 — correctOptionIndex yalnız burada) */}
          <Card>
            <CardContent>
              <QuizEditor />
            </CardContent>
          </Card>

          {/* Sticky alt aksiyon çubuğu — owner review B4-3: `max-w-[960px]` sarmalayıcının İÇİNDE
              (`RiskAssessmentEditorPage.tsx`'in AYNI deseni) — `<main>` sayfanın TEK kaydırıcısı
              (`AppLayout.tsx`), çubuk onun altına SABİTLENİR ama artık form sütununu AŞMAZ.
              `border-border-strong` (owner review B4-4) — hairline `border-border` beyaz Card'ın
              üstünde çubuğu altındaki alanın bir PARÇASIYMIŞ gibi gösteriyordu. */}
          <div className="sticky bottom-0 z-[var(--z-base)] flex items-center justify-end gap-2 border-t border-border-strong bg-surface px-4 py-3 sm:px-6">
            <Button
              type="button"
              variant="secondary"
              disabled={mutation.isPending}
              onClick={() => navigate('/trainings')}
            >
              {t('trainings.common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {mutation.isPending ? (
                <Spinner label={t('trainings.common.save')} />
              ) : isEdit ? (
                t('trainings.common.save')
              ) : (
                t('trainings.common.create')
              )}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
